// LE SCRITTURE del Burraco nella Hub (PR2 dell'ingresso; condizioni Z8-Z10 di tma-orcb): «Gioca», «Invita un amico» ed «Entra con un codice». Forma del corpo, delle risposte e
// dei guasti; funzioni PURE, niente rete: le mutation stanno in `services/limuloApi.ts` (`createBurracoTable`, `joinBurracoTable`), il consumatore in
// `components/burraco/GiocaBurraco.tsx`.
//
// ⚠️ LE RISPOSTE SONO IPOTESI (Z8): nessuno ha mai misurato una POST (collaudo-b e il runner non scrivono; la prima prova e di tma-orcb, dal vivo). Qui si segue il CODICE del resolver
// (QBurraco `limulo-app/backend/resolvers.js` @ 83ab2d23: `tableCreate`, `tableJoin`) e il suo README (`limulo-app/README.md:72-73`):
//   create  corpo   { tipo: 'casual' | 'privato', posti: 2 | 4, rulesetId?, soloPersone?, idempotencyKey? }
//           risposta { provider, data: { tableId, posto, posti, tipo, classificato, invito, wsUrl } }   (busta di un game host: si scende con `capabilityPayloadOf`)
//   join    corpo   { invito?, idempotencyKey? }  col `tableId` nel PERCORSO (`POST /v1/games/burraco/tables/:tableId/seats`)
//           risposta { provider, data: { tableId, posto, wsUrl } }
// Il tavolo di GRUPPO non e servito (rev28 F2): non c'e e non si manda mai `tipo: 'gruppo'`. Il client NON manda `classificato` (lo decide la faccia dal tipo), ne `fascia`, ne `posto`,
// ne il livello dei bot: sono campi che il resolver RIFIUTA (`burraco.campo-non-del-client`).
// Il `wsUrl` si IGNORA: l'indirizzo del socket e quello del ticket (`lib/burraco/ticket.ts`).

import { burracoTableIdValido } from './ticket';
import { limuloFailure } from '@/lib/limulo/errors';

/** La rotta di creazione. Una POST: non si ritenta da sola (`limuloBaseQuery` non ritenta nessuna POST). */
export const BURRACO_TAVOLI_ROUTE = '/v1/games/burraco/tables';
/** La rotta di ingresso: il `tableId` sta nel percorso, MINUSCOLO (il game host pretende l'uuid canonico). */
export const burracoPostiRoute = (tableId: string): string => `${BURRACO_TAVOLI_ROUTE}/${tableId.toLowerCase()}/seats`;

/**
 * I posti (`posti` e obbligatorio e il resolver accetta 2 o 4). «Gioca» apre un casual a 2, uno contro uno: SCELTA DI LUCA, 12/10, nel terminale di
 * qburraco-orcb, testuale «1 contro 1» (prima era 4, il default del cantiere -39). Il posto libero lo prende un robot dopo ~60 s (`BOT_FILL_AFTER_MS`).
 * «Invita un amico» apre un privato a 2 (uno contro uno: un solo invitato, e nel privato non entra mai un robot). Sono due costanti, una per scelta.
 */
export const POSTI_GIOCA = 2;
/** «Gioca 2 contro 2»: un casual a 4, accanto all'uno contro uno (Luca, 12/10: «e fare anche gioca per 2 vs 2?»). I posti liberi li prendono i robot. */
export const POSTI_GIOCA_2V2 = 4;
export const POSTI_INVITO = 2;

/** I due tipi che la TMA crea. ⛔ `gruppo` non c'e: non e servito (501) e non si manda. */
export type TipoTavolo = 'casual' | 'privato';

/**
 * La chiave di idempotenza di UN'azione dell'utente (8..200 caratteri di `A-Za-z0-9._:@-`, nel CORPO come campo `idempotencyKey`: il resolver la gira al game host come
 * `Idempotency-Key`, K4 `withIdempotency`). La sceglie il client UNA volta per azione e la RIUSA identica se il tentativo si ripete dopo una risposta persa: una nuova a ogni richiesta
 * duplicherebbe il tavolo. Il game host la lega alla PERSONA, quindi la chiave di un altro non apre niente.
 */
export const burracoIdempotencyKey = (): string => `tma-${crypto.randomUUID()}`;

export function creaTavoloBody(a: { tipo: TipoTavolo; posti: number; idempotencyKey: string }): { tipo: TipoTavolo; posti: number; idempotencyKey: string } {
  return { tipo: a.tipo, posti: a.posti, idempotencyKey: a.idempotencyKey };
}
export function entraBody(a: { invito: string; idempotencyKey: string }): { invito: string; idempotencyKey: string } {
  return { invito: a.invito, idempotencyKey: a.idempotencyKey };
}

const asRecord = (v: unknown): Record<string, unknown> => (v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const testo = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);

export interface TavoloCreato {
  /** L'uuid del tavolo, MINUSCOLO: e l'`id` della rotta `burraco/tavolo/:id`. */
  tableId: string;
  /** Il codice d'invito di un tavolo PRIVATO, reso UNA volta a chi crea (il game host ne tiene solo lo sha256); `null` = assente (un replay col tavolo pieno, o un casual). */
  invito: string | null;
}

/**
 * Il tavolo creato dal `data` del game host (gia sceso dalla busta). Senza un `tableId` uuid e un `posto` intero e `null` (un errore col suo nome, mai un `/burraco/tavolo/undefined`).
 * `invito` assente o vuoto = `null`: «assente ≠ vuoto» nel resolver, e qui nessuno dei due e un codice.
 */
export function tavoloCreatoOf(data: unknown): TavoloCreato | null {
  const r = asRecord(data);
  const id = testo(r.tableId);
  if (id === null || !burracoTableIdValido(id) || !Number.isInteger(r.posto)) return null;
  return { tableId: id.toLowerCase(), invito: testo(r.invito) };
}

export interface IngressoRiuscito {
  tableId: string;
}

/**
 * L'ingresso riuscito dal `data` del game host. Il `tableId` e quello del PERCORSO che si e chiesto (il resolver lo rimanda uguale); senza un `posto` intero la risposta non si legge (`null`).
 */
export function ingressoOf(data: unknown, tableId: string): IngressoRiuscito | null {
  const r = asRecord(data);
  if (!Number.isInteger(r.posto) || !burracoTableIdValido(tableId)) return null;
  const rimandato = testo(r.tableId);
  if (rimandato !== null && rimandato.toLowerCase() !== tableId.toLowerCase()) return null; // un altro tavolo da quello chiesto: non ci si siede alla cieca
  return { tableId: tableId.toLowerCase() };
}

// ── Il CODICE da condividere ────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Il join vuole il `tableId` E l'invito (`{ tableId, invito }`), e il game host rende solo l'invito (24 caratteri base64url): da solo non apre niente. Il «codice» che l'utente copia e incolla e quindi
// UNA stringa con tutti e due: `<tableId senza trattini>.<invito>` (32 esadecimali, un punto, l'invito; il punto non e ne esadecimale ne base64url, quindi separa senza ambiguita). E una convenzione
// NOSTRA (la TMA scrive e la TMA legge: il game host non la conosce), che si legge anche con i trattini dell'uuid. ⚠️ Il codice e una credenziale: non va in log, in storage, nell'URL.

/** I caratteri di un invito, come li accetta il game host (`min(1).max(200)`) e come li emette (`base64url`). */
const INVITO = /^[A-Za-z0-9_-]{1,200}$/;
const ESA32 = /^[0-9a-fA-F]{32}$/;

export function codiceInvito(tableId: string, invito: string): string {
  return `${tableId.replace(/-/g, '').toLowerCase()}.${invito}`;
}

export interface CodiceLetto {
  tableId: string;
  invito: string;
}

/** Il codice incollato → `{ tableId, invito }`, o `null` se non ha la forma. Spazi attorno ignorati; il tableId anche con i trattini; mai un invito senza tableId. */
export function leggiCodiceInvito(testoIncollato: string): CodiceLetto | null {
  const t = testoIncollato.trim();
  const i = t.indexOf('.');
  if (i <= 0) return null;
  const sinistra = t.slice(0, i);
  const invito = t.slice(i + 1);
  if (!INVITO.test(invito)) return null;
  const nudo = sinistra.replace(/-/g, '');
  if (!ESA32.test(nudo) || (sinistra.includes('-') && sinistra.length !== 36)) return null;
  const n = nudo.toLowerCase();
  const tableId = `${n.slice(0, 8)}-${n.slice(8, 12)}-${n.slice(12, 16)}-${n.slice(16, 20)}-${n.slice(20)}`;
  return burracoTableIdValido(tableId) ? { tableId, invito } : null;
}

// ── I guasti ────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
/**
 * Perche una scrittura non e andata (Z8: il codice sta in `code`, `error` e il messaggio; il BFF appiattisce ogni 4xx del game host in `E_QLASH_HTTP_<status>`, senza `details`: si decide sullo STATUS):
 *  · `non-disponibile` — SOLO `424 E_QLASH_CAPABILITY_DISABLED`: uno stato, non un guasto; riprovare non serve;
 *  · `rete` — niente status HTTP (rete caduta, o scaduta l'attesa);
 *  · `conflitto` (409) — creando: si siede GIA a un tavolo vivo; entrando: tavolo pieno o gia seduto (il BFF non li distingue);
 *  · `vietato` (403) — creando: chi crea e sospeso (`SUSPENDED`) o la capability non e abilitata (anch'essa 403: indistinguibili);
 *  · `nonTrovato` (404) — entrando: il codice e sbagliato, o il tavolo non c'e o e finito (UNA risposta, di proposito); creando non puo darsi;
 *  · `richiesta` (400/422), `troppeRichieste` (429), `server` (5xx), `errore` (ogni altro, un altro 424 compreso).
 * ⛔ Un 424 qualunque NON e «non disponibile»: il BFF fa uscire col 424 ogni guasto del provider (lezione di TMA#691 e T1c).
 */
export type GuastoScrittura = 'non-disponibile' | 'rete' | 'conflitto' | 'vietato' | 'nonTrovato' | 'richiesta' | 'troppeRichieste' | 'server' | 'errore';

export function guastoScritturaOf(errore: unknown): GuastoScrittura {
  const f = limuloFailure(errore);
  if (f.status === 424 && f.code === 'E_QLASH_CAPABILITY_DISABLED') return 'non-disponibile';
  const s = f.status;
  if (s === null) return 'rete';
  if (s === 409) return 'conflitto';
  if (s === 403) return 'vietato';
  if (s === 404) return 'nonTrovato';
  if (s === 400 || s === 422) return 'richiesta';
  if (s === 429) return 'troppeRichieste';
  if (s >= 500) return 'server';
  return 'errore';
}

/**
 * La chiave di idempotenza si TIENE per il tentativo successivo solo se la risposta puo essersi persa o se il guasto e passeggero: rete, 5xx, 424 (un guasto del resolver, la richiesta puo
 * essere arrivata). Un rifiuto DEFINITIVO (un 4xx che non e 424) la scarta: ripeterlo con la stessa chiave rimanderebbe lo stesso rifiuto, o peggio un esito salvato.
 */
export function teniamoLaChiave(errore: unknown): boolean {
  const s = limuloFailure(errore).status;
  return s === null || s === 424 || s >= 500;
}
